// Types for Trips Module

export type AssignmentStatus = "pending" | "active" | "completed" | "cancelled";

// Timezone codes for assignments
export const TIMEZONE_OPTIONS = {
  // Mexico
  'America/Mexico_City': 'MX - Ciudad de México',
  'America/Cancun': 'MX - Cancún (Quintana Roo)',
  'America/Tijuana': 'MX - Tijuana (Baja California)',
  'America/Hermosillo': 'MX - Hermosillo (Sonora)',
  'America/Monterrey': 'MX - Monterrey',
  'America/Merida': 'MX - Mérida',
  'America/Chihuahua': 'MX - Chihuahua',
  // USA
  'America/New_York': 'US - Eastern Time',
  'America/Chicago': 'US - Central Time',
  'America/Denver': 'US - Mountain Time',
  'America/Los_Angeles': 'US - Pacific Time',
  'America/Phoenix': 'US - Arizona',
  'America/Anchorage': 'US - Alaska',
  'Pacific/Honolulu': 'US - Hawaii',
  'America/Detroit': 'US - Detroit',
  'America/Indiana/Indianapolis': 'US - Indiana',
  'America/Boise': 'US - Boise',
  'America/El_Paso': 'US - El Paso',
  // Latin America
  'America/Caracas': 'VE - Venezuela',
  'America/La_Paz': 'BO - Bolivia',
  'America/Bogota': 'CO - Colombia',
  'America/Lima': 'PE - Perú',
  'America/Santiago': 'CL - Chile',
  'America/Argentina/Buenos_Aires': 'AR - Argentina',
  'America/Sao_Paulo': 'BR - Brasil',
  'America/Guayaquil': 'EC - Ecuador',
  'America/Asuncion': 'PY - Paraguay',
  'America/Montevideo': 'UY - Uruguay',
  'America/Panama': 'PA - Panamá',
  'America/Guatemala': 'GT - Guatemala',
  'America/Managua': 'NI - Nicaragua',
  'America/Tegucigalpa': 'HN - Honduras',
  'America/El_Salvador': 'SV - El Salvador',
  'America/Costa_Rica': 'CR - Costa Rica',
  'America/Santo_Domingo': 'DO - República Dominicana',
  'America/Havana': 'CU - Cuba',
  // Canada
  'America/Toronto': 'CA - Toronto',
  'America/Vancouver': 'CA - Vancouver',
  'America/Edmonton': 'CA - Edmonton',
  'America/Winnipeg': 'CA - Winnipeg',
  // Europe
  'Europe/Madrid': 'ES - España',
  'Europe/London': 'UK - Reino Unido',
  'UTC': 'UTC',
} as const;

export type TimezoneCode = keyof typeof TIMEZONE_OPTIONS;

export interface TripAssignment {
  id?: string;
  device_id: string;
  date: string;           // "2026-04-20"
  start_time: string;     // "2026-04-20T08:00"
  end_time: string;       // "2026-04-20T10:00"
  status: AssignmentStatus;
  timezone?: TimezoneCode; // Timezone for this assignment
  completed_at?: number;
  cancelled_at?: number;
  closed_by?: string;
  closed_by_user_id?: string;
  driver_user_id?: string;
  signature?: string;
  comment?: string;
  experience?: number | string | null;
  evidence_by_user_id?: string;
  evidence_at?: number;
  device?: {
    id: string;
    name?: string;
    identifier?: string;
    vehicle_id?: string | null;
    remolque_id?: string | null;
    vehicle?: { id: string; name?: string } | null;
    remolque?: { id: string; name?: string } | null;
  } | null;
  /**
   * Conductor resuelto en backend (assignment.device → device.vehicle_id →
   * drivers → users). Lo usa el chat del viaje para preseleccionar al
   * driver como participante. Ver `tripsController._resolveDriversForVehicleIds`.
   */
  driver?: {
    id: string | null;
    user_id: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
}

export interface Trip {
  id: string;
  title?: string;
  description?: string;
  route_id?: string;
  // Cotizador de costos operativos: se llenan al aceptar una cotización.
  quote_id?: string | null;
  price?: number | null;
  estimated_cost?: number | null;
  status_id?: string;
  priority?: string;
  company_id?: string;
  comment?: string;
  assignments?: TripAssignment[];
  notifiedUsers?: string[];
  sendEmails?: boolean;
  createdAt?: { _seconds: number; _nanoseconds: number } | string;
  updatedAt?: { _seconds: number; _nanoseconds: number } | string;
  // Populated relations
  status?: {
    id: string;
    name?: string;
    color?: string;
  } | null;
  route?: {
    id: string;
    name?: string;
    description?: string;
  } | null;
  // Legacy fields (kept for backwards compatibility)
  origin?: string;
  destiny?: string;
  eta?: string;
  vehicle_id?: string;
  remolque_id?: string;
  geofence_ids?: string[];
  geofence_id?: string;
  charge_ids?: string[];
  user_id?: string;
  experience?: string;
  signature?: string;
  startedAt?: string;
  finalizedAt?: string;
  /** Viaje creado por el Cargo Market al confirmar una carga (unidad + operador). */
  cargoMarket?: { tripId: string; loadId: string; folio: string; shipperName?: string } | null;
  vehicle?: {
    id: string;
    name?: string;
    plate?: string;
    image?: string;
  } | null;
  remolque?: {
    id: string;
    name?: string;
    plate?: string;
  } | null;
  geofence?: {
    id: string;
    name?: string;
    shape?: string;
  } | null;
}

export interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

export interface TripAssignmentFormData {
  device_id: string;
  date: string;
  start_time: string;
  end_time: string;
  status?: AssignmentStatus;
  timezone?: TimezoneCode;
}

export interface TripFormData {
  title: string;
  description?: string;
  route_id: string;
  status_id: string;
  priority: string;
  shipment_ids?: string[];
  assignments?: TripAssignmentFormData[];
  notifiedUsers?: string[];
  sendEmails?: boolean;
  comment?: string;
  event_file?: File | null;
}

export interface FilterParams {
  page: number;
  limit: number;
  status_id?: string;
  search?: string;
  device_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface TripMeta {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  itemsPerPage: number;
}

export type ViewType = "grid" | "list";
export type TripPriority = "low" | "medium" | "high" | "urgent";
